import * as api from "./client.js";

/** BullMQ job states the queue endpoints can be filtered by. */
export const EDITORIAL_TRANSFORM_JOB_STATUSES = [
  "waiting",
  "active",
  "completed",
  "failed",
  "delayed",
  "paused",
] as const;

export type EditorialTransformJobStatus = (typeof EDITORIAL_TRANSFORM_JOB_STATUSES)[number];

/** Derived per-job result the backend exposes alongside the raw BullMQ state. */
export const EDITORIAL_TRANSFORM_OUTCOMES = [
  "transformed",
  "content_unchanged",
  "manual_entry_protected",
  "edited_row_protected",
  "would_insert",
  "would_update",
  "lawyer_led",
  "contact_unlinked",
  "unresolved_no_firm_ref",
  "unresolved_firm_ref_not_found",
  "company_unresolved",
  "failed",
  "unknown",
] as const;

export type EditorialTransformOutcome = (typeof EDITORIAL_TRANSFORM_OUTCOMES)[number];

/** Payload every editorial-transform job carries. */
export type EditorialTransformJobData = {
  parseResultId?: string;
  submissionId?: string;
  forceOverwrite?: boolean;
  dryRun?: boolean;
};

/** The BullMQ `returnvalue` the worker persists: a transformed result or a skip marker. */
export type EditorialTransformReturnValue =
  | { parentId: string; rowsCreated: number; companyId: string; researchBpaId: string }
  | { skipped: string; dryRun?: true }
  | ({ dryRun: true; wouldDo: "insert" } & EditorialTransformPreviewedFields)
  | ({
      dryRun: true;
      wouldDo: "update";
      existingRowId: string;
    } & EditorialTransformPreviewedFields);

export type EditorialTransformPreviewedFields = {
  rankingTableId?: string | null;
  officeId?: string | null;
};

/** Submission context the backend joins onto each job for the detail panel. */
export type EditorialTransformJobEnrichment = {
  submissionId: string;
  firmRef: string | null;
  firmName: string;
  publicationId: number;
  practiceAreaId: number | null;
  createdBy: string | null;
};

export type EditorialTransformFailedJob = {
  id: string | null;
  name: string;
  failedReason: string | null;
  attemptsMade: number;
  timestamp: number | null;
  finishedOn: number | null;
  data: EditorialTransformJobData | null;
};

export type EditorialTransformActiveJob = {
  id: string | null;
  name: string;
  timestamp: number | null;
  processedOn: number | null;
  data: EditorialTransformJobData | null;
};

export type EditorialTransformQueueStats = {
  name: string;
  counts: Record<EditorialTransformJobStatus, number>;
  previewCounts?: { completed: number; failed: number };
  lastExecutedAt: number | null;
  completedLastMinute: number;
  recentFailedJobs: EditorialTransformFailedJob[];
  activeJobs: EditorialTransformActiveJob[];
};

export type EditorialTransformQueueStatsResponse = {
  queue: EditorialTransformQueueStats;
  requestId?: string | null;
};

export type EditorialTransformBackfillRequest = {
  dryRun?: boolean;
  limit?: number;
  publicationId?: number;
  forceOverwrite?: boolean;
  parseResultIds?: string[];
};

export type EditorialTransformBackfillCandidate = {
  parseResultId: string;
  submissionId: string;
};

export type EditorialTransformBackfillResponse = {
  total: number;
  enqueued: number;
  skipped: number;
  dryRun: boolean;
  candidates: EditorialTransformBackfillCandidate[];
  requestId?: string | null;
};

/** A single job from the paginated jobs endpoint. */
export type EditorialTransformJob = {
  id: string | null;
  name: string;
  timestamp: number | null;
  processedOn: number | null;
  finishedOn: number | null;
  attemptsMade: number;
  failedReason: string | null;
  returnvalue: EditorialTransformReturnValue | null;
  outcome: EditorialTransformOutcome;
  data: EditorialTransformJobData | null;
  enrichment: EditorialTransformJobEnrichment | null;
};

export type EditorialTransformJobsQuery = {
  status?: EditorialTransformJobStatus;
  /** 1-based. */
  page?: number;
  /** 1..100. */
  pageSize?: number;
  preview?: "only" | "exclude";
};

export type EditorialTransformJobsResponse = {
  jobs: EditorialTransformJob[];
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    hasMore: boolean;
  };
  requestId?: string | null;
};

export const editorialTransform = {
  getQueueStats(): Promise<EditorialTransformQueueStatsResponse> {
    return api.get<EditorialTransformQueueStatsResponse>(
      "/v1/submission/doc-parsing/editorial-transform/queue-stats",
    );
  },

  getJobs(query: EditorialTransformJobsQuery = {}): Promise<EditorialTransformJobsResponse> {
    const params = new URLSearchParams();
    if (query.status) params.append("status", query.status);
    if (query.page !== undefined) params.append("page", String(query.page));
    if (query.pageSize !== undefined) params.append("pageSize", String(query.pageSize));
    if (query.preview) params.append("preview", query.preview);
    const search = params.toString();
    return api.get<EditorialTransformJobsResponse>(
      `/v1/submission/doc-parsing/editorial-transform/jobs${search ? `?${search}` : ""}`,
    );
  },

  backfill(body: EditorialTransformBackfillRequest): Promise<EditorialTransformBackfillResponse> {
    return api.post<EditorialTransformBackfillResponse>(
      "/v1/submission/doc-parsing/editorial-transform/backfill",
      body,
    );
  },
};
